import * as React from 'react';
import { FC } from 'react';

import { format, parseISO } from 'date-fns';
import { StyleSheet } from 'react-native';
import { Select } from 'ucl/components/select';

import ActionButton, {
  ActionButtonSizes,
  ActionButtonVariants,
  ActionLink,
} from 'components/action-button';
import { ArrowButton } from 'components/arrow-button';
import { List } from 'components/list';
import { Box, Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { LOCALES } from 'state/intl/types';
import { spacing } from 'styles/constants';
import { getLocale } from 'utils/intl/language';
import { localeStringToDateFnsLocale } from 'utils/intl/locale-string-to-date-fns-locale';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { HeadingStyled, RecentOrdersDescriptionText } from './styled';
import { ISupportV2OrdersListViewProps } from './types';

const dateFormatForLocale = (locale: string) => (locale === LOCALES.fr_ca ? 'd MMMM' : 'MMMM d');

export const SupportV2OrdersListView: FC<React.PropsWithChildren<
  ISupportV2OrdersListViewProps
>> = ({
  dropdownOptions,
  items,
  onYearAndMonthChange,
  selectedYearAndMonth,
  onSelectOrder = noop,
  onClickSignin = noop,
  isAuthenticated = false,
}) => {
  return (
    <Box style={styles.contentWrap}>
      <Box style={styles.backLinkWrapper}>
        <ActionLink
          size={ActionButtonSizes.SMALL}
          to={routes.support}
          variant={ActionButtonVariants.OUTLINE}
        >
          {t('backToTopics')}
        </ActionLink>
      </Box>
      <HeadingStyled testID="section-heading">{t('recentOrders')}</HeadingStyled>
      <RecentOrdersDescriptionText>{t('refundEligibilityInfo')}</RecentOrdersDescriptionText>
      {isAuthenticated ? (
        <Box style={styles.filterContent}>
          <Typography>{t('filterByMonth')}</Typography>
          <Select
            testID="year-and-month-filter"
            onValueChange={onYearAndMonthChange}
            selectedValue={selectedYearAndMonth}
          >
            {dropdownOptions.map(o => (
              <Select.Item key={`${o.value}+${o.label}`} value={o.value} label={o.label} />
            ))}
          </Select>

          <List marginBottom="0">
            {items.map(item => (
              <ArrowButton
                onPress={() => onSelectOrder(item)}
                key={item._id}
                testID="order-list-item-link"
              >
                <Box>
                  <Typography>
                    {format(parseISO(item.createdAt), dateFormatForLocale(getLocale()), {
                      locale: localeStringToDateFnsLocale(getLocale()),
                    })}
                  </Typography>
                  <Typography>{t('xItems', { xItems: item.cart.cartEntries?.length })}</Typography>
                </Box>
              </ArrowButton>
            ))}
          </List>
        </Box>
      ) : (
        <ActionButton onPress={onClickSignin} testID="sign-in-button">
          {t('signInToViewOrders')}
        </ActionButton>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  contentWrap: {
    marginTop: spacing.$8,
    marginBottom: 0,
    marginHorizontal: spacing.$4,
    flex: 1,
  },
  backLinkWrapper: {
    marginHorizontal: spacing.$20,
    marginBottom: spacing.$4,
  },
  filterContent: {
    flex: 1,
  },
});
